import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Shell from "../components/Shell.jsx";
import { api } from "../lib/api.js";
import { useAuth } from "../context/AuthContext.jsx";
import { pushStatus, enablePush, disablePush } from "../lib/push.js";

// The demo never connects to anything — not the owner's accounts, not a
// visitor's. Each connect-type card swaps its button for a plain description
// of what the live app does once connected, so the value is visible without
// anyone's mailbox, bank, or phone being touched.
function DemoNote({ children }) {
  return <p className="mt-1 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">{children}</p>;
}

function DemoOverviewCard() {
  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
      <p className="text-sm font-semibold text-amber-900">🧪 You're in the demo</p>
      <p className="mt-1 text-xs text-amber-900/80">
        Sample business, sample numbers, and nothing connected — the demo cannot reach a real mailbox, bank, Stripe
        account, calendar, or phone, yours or anyone's. In the live app, this page is where the owner connects:
      </p>
      <ul className="mt-2 space-y-1 text-xs text-amber-900/90">
        <li>• <strong>Microsoft 365</strong> — client emails log themselves to contacts, requests become follow-up tasks, sent replies are filed, and invoice emails appear in Outlook Drafts with the PDF attached.</li>
        <li>• <strong>Stripe</strong> (read-only) — every payment syncs hourly, posts with its fee in a tap, and payouts reconcile against the bank.</li>
        <li>• <strong>Bank statements & alerts</strong> — imported lines match money already in the books; only what's new needs a decision.</li>
        <li>• <strong>Google Drive</strong> — a scheduled writing routine drops news-post drafts straight into the social composer.</li>
        <li>• <strong>Push reminders & Face ID</strong> — due follow-ups reach the phone; sign-in skips the PIN.</li>
      </ul>
    </div>
  );
}

function InboxWatchCard() {
  const { user } = useAuth();
  const [st, setSt] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api("/api/ms/status").then(setSt).catch(() => setSt(null));
  useEffect(() => {
    load();
    const q = new URLSearchParams(window.location.search);
    if (q.get("inbox") === "connected") setNote("✅ Inbox connected — watching for client email from now on.");
    if (q.get("inbox_error")) setNote(`⚠️ ${q.get("inbox_error")}`);
  }, []);

  if (user?.demo)
    return (
      <div className="rounded-2xl bg-white p-5 shadow-sm">
        <p className="text-sm font-semibold text-slate-700">📥 Inbox watch</p>
        <DemoNote>
          Demo mode — connecting a mailbox is turned off here. In the live app, Microsoft 365 connects once and the
          inbox watch logs client emails to their contacts, flags requests as follow-up tasks, files your sent
          replies, and writes invoice emails into Outlook Drafts for you to send.
        </DemoNote>
      </div>
    );

  // Hidden until the Azure app credentials are configured in Cloudflare.
  if (!st?.configured) return null;

  async function checkNow() {
    setBusy(true);
    setNote("");
    try {
      const r = await api("/api/ms/sync", { method: "POST" });
      setNote(r.matched ? `Logged ${r.matched} client email${r.matched > 1 ? "s" : ""}${r.actions ? `, flagged ${r.actions} for action` : ""}.` : "No new client email.");
      load();
    } catch (e) {
      setNote(`⚠️ ${e.message}`);
    } finally {
      setBusy(false);
    }
  }
  async function disconnect() {
    if (!window.confirm("Stop watching this inbox?")) return;
    await api("/api/ms/status", { method: "DELETE" });
    setNote("");
    load();
  }

  return (
    <div className="rounded-2xl bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-700">📥 Inbox watch</p>
          <p className="text-xs text-slate-400">
            {st.connected
              ? `Watching ${st.mailbox}${st.last_sync_at ? ` · last check ${st.last_sync_at}` : ""} — client emails log to their contact; action requests become tasks.`
              : "Connect Microsoft 365 to auto-log client emails and flag action requests."}
          </p>
          {note && <p className="mt-1 text-xs text-slate-600">{note}</p>}
        </div>
        {st.connected ? (
          <div className="flex shrink-0 flex-col gap-1.5">
            <button onClick={checkNow} disabled={busy} className="rounded-xl bg-navy-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
              {busy ? "Checking…" : "Check now"}
            </button>
            <button onClick={disconnect} className="text-xs font-semibold text-slate-400">
              Disconnect
            </button>
          </div>
        ) : (
          <a href="/api/ms/connect" className="shrink-0 rounded-xl bg-navy-700 px-4 py-2 text-sm font-semibold text-white">
            Connect
          </a>
        )}
      </div>
    </div>
  );
}

function RemindersCard() {
  const { user } = useAuth();
  const [status, setStatus] = useState("checking");
  const [error, setError] = useState("");

  useEffect(() => {
    pushStatus().then(setStatus);
  }, []);

  if (user?.demo)
    return (
      <div className="rounded-2xl bg-white p-5 shadow-sm">
        <p className="text-sm font-semibold text-slate-700">🔔 Follow-up reminders</p>
        <DemoNote>
          Demo mode — reminders aren't delivered here, and nothing is registered on your device. In the live app,
          a morning push notification lists due follow-ups, open trips, and money waiting for review.
        </DemoNote>
      </div>
    );

  async function toggle() {
    setError("");
    try {
      if (status === "enabled") {
        await disablePush();
        setStatus("off");
      } else {
        await enablePush();
        setStatus("enabled");
      }
    } catch (e) {
      setError(e.message || "Couldn't change notification settings.");
      pushStatus().then(setStatus);
    }
  }

  if (status === "unsupported" || status === "checking") return null;
  return (
    <div className="rounded-2xl bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-slate-700">🔔 Follow-up reminders</p>
          <p className="text-xs text-slate-400">
            {status === "enabled"
              ? "On for this device — due follow-ups arrive each morning."
              : status === "blocked"
                ? "Blocked in system settings for this app."
                : "Get a morning notification when follow-ups are due."}
          </p>
          {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
        </div>
        {status !== "blocked" && (
          <button
            onClick={toggle}
            className={`shrink-0 rounded-xl px-4 py-2 text-sm font-semibold ${
              status === "enabled" ? "border border-slate-300 text-slate-500" : "bg-navy-700 text-white"
            }`}
          >
            {status === "enabled" ? "Turn off" : "Enable"}
          </button>
        )}
      </div>
    </div>
  );
}

const LIVE = [
  { to: "/dashboard", icon: "📊", title: "Dashboard", desc: "P&L, income vs. expenses, recent entries" },
  { to: "/ledger", icon: "📒", title: "Ledger", desc: "All income & expense entries, search, P&L" },
  { to: "/money", icon: "💰", title: "Money", desc: "Stripe, bank import, invoices & who owes you" },
  { to: "/tithe", icon: "🙏", title: "Tithe", desc: "Monthly 10% of what the business keeps after expenses and tax" },
  { to: "/deductions", icon: "🏠", title: "Deductions", desc: "Home office, subscriptions, asset register" },
  { to: "/reconcile", icon: "⚖️", title: "Reconcile", desc: "Why Net isn't your bank balance, and an audit of the books against your statements" },
  { to: "/export", icon: "📦", title: "CPA export", desc: "Year-end package — one ZIP for filing" },
  { to: "/crm", icon: "👥", title: "CRM", desc: "Contacts, engagement pipeline, follow-ups" },
  { to: "/mileage", icon: "🚗", title: "Mileage", desc: "Odometer snaps, trip log, YTD deduction" },
  { to: "/social", icon: "📣", title: "Social", desc: "Idea → platform drafts → scheduled posting" },
  { to: "/audit", icon: "📜", title: "Activity log", desc: "Every change — who, what, when" },
];

const UPCOMING = []; // every planned phase is live — W-9/NDA e-sign lives on each contact's page

export default function More() {
  const { user } = useAuth();
  return (
    <Shell title="More">
      {user?.demo && <DemoOverviewCard />}
      <RemindersCard />
      <InboxWatchCard />
      <div className="divide-y divide-slate-100 rounded-2xl bg-white shadow-sm">
        {LIVE.map((m) => (
          <Link key={m.to} to={m.to} className="flex items-center gap-3 px-4 py-4">
            <span className="text-2xl">{m.icon}</span>
            <div className="min-w-0 flex-1">
              <p className="font-medium text-slate-800">{m.title}</p>
              <p className="truncate text-xs text-slate-400">{m.desc}</p>
            </div>
            <span className="text-slate-300">›</span>
          </Link>
        ))}
      </div>

      {UPCOMING.length > 0 && (
        <div className="rounded-2xl bg-white p-5 shadow-sm">
          <p className="text-sm font-semibold text-slate-700">Coming next</p>
          <ul className="mt-3 space-y-3">
            {UPCOMING.map(([title, desc]) => (
              <li key={title}>
                <p className="text-sm font-medium text-slate-800">{title}</p>
                <p className="text-xs text-slate-400">{desc}</p>
              </li>
            ))}
          </ul>
        </div>
      )}
      <p className="text-center text-xs text-slate-400">Estimates shown in this app are not tax advice — confirm with your CPA.</p>
      {/* Injected at build time (vite define) — confirms which publish is running. */}
      <p className="text-center text-[11px] text-slate-300">Version {__BUILD_STAMP__}</p>
    </Shell>
  );
}
